import { useUserIdParamsOrThrow } from "../useUserIdParamsOrThrow.ts";
import { useConfirm } from "../../UI/Feedback/useConfirm.ts";
import { useUserClassRoomPlans } from "./useUserClassRoomPlans.ts";
import { FullPageLoading } from "../../UI/Loading/FullPageLoading.tsx";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { useUserById } from "../useUserById.ts";
import { ClassRoomPlanListItem } from "./ClassRoomPlanListItem.tsx";
import { BreadCrumbsWrapper } from "../../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../../UI/Layout/BreadCrumbsItem.tsx";
import { useToAllUsers } from "../useToAllUsers.ts";
import { InfoFeedback } from "../../UI/Feedback/InfoFeedback.tsx";
import { useToCreateClassRoomPlanScreen } from "./useToCreateClassRoomPlanScreen.ts";
import { useUserRemainingLessons } from "./useUserRemainingLessons.ts";
import _capitalize from "lodash.capitalize";
import { Card } from "../../UI/Card.tsx";
import { useDeleteClassRoomPlan } from "./useDeleteClassRoomPlan.ts";
import { UserFrozenWeekComponent } from "./FreezeWeek/UserFrozenWeekComponent.tsx";
import { useToUpdateClassRoomPlanScreen } from "./useToUpdateClassRoomPlanScreen.ts";
import { useDetachLastMergedPlan } from "./useDetachLastMergedPlan.ts";
import { useMergeUnlimitedPlans } from "./useMergeUnlimitedPlans.ts";
import { useToast } from "../../UI/Feedback/useToast.ts";
import { ClassRoomPlanFragmentFragment } from "../../gql/graphql.ts";
import { formatDate } from "../../UI/Utils/formatDate.ts";
import dayjs from "dayjs";

export const AllUserClassRoomPlanScreenPath = "/users/:userId/class-room-plans";
export function AllUserClassRoomPlanScreen() {
  const { userId } = useUserIdParamsOrThrow();
  const {
    classRoomPlans,
    loading: loadingClassRoomPlans,
    refetch,
  } = useUserClassRoomPlans(userId);
  const { user, loading: loadingUser } = useUserById(userId);
  const { remainingLessons, loading: loadingRemainingLessons } =
    useUserRemainingLessons(userId);
  const loading =
    loadingClassRoomPlans || loadingUser || loadingRemainingLessons;

  const { toUpdatePlanScreen } = useToUpdateClassRoomPlanScreen();

  const { toCreateClassRoomPlan } = useToCreateClassRoomPlanScreen();
  const { toAllUsers } = useToAllUsers();

  const { deleteClassRoomPlan, loading: deletingPlan } =
    useDeleteClassRoomPlan();
  const askConfirmation = useConfirm();
  async function onDeletePlan(planId: string) {
    if (
      await askConfirmation({
        title: "Delete this plan?",
        confirmLabel: "Delete",
        isDestructive: true,
      })
    ) {
      await deleteClassRoomPlan(planId);
      await refetch();
    }
  }

  const { detachLastMergedPlan, loading: detachingLastMerge } =
    useDetachLastMergedPlan();
  const { mergeUnlimitedPlans, loading: mergingPlans } =
    useMergeUnlimitedPlans();
  const showToast = useToast();

  const plansByStartDate = [...classRoomPlans].sort(
    (first, second) =>
      dayjs(first.startedAtWeekUtc).valueOf() -
      dayjs(second.startedAtWeekUtc).valueOf(),
  );

  function nextPlanOf(
    plan: ClassRoomPlanFragmentFragment,
  ): ClassRoomPlanFragmentFragment | undefined {
    return plansByStartDate[
      plansByStartDate.findIndex((p) => p.id === plan.id) + 1
    ];
  }

  function canDetachLastMerge(plan: ClassRoomPlanFragmentFragment): boolean {
    return (
      !!plan.unlimitedLessonsPerWeekEnabled &&
      (plan.mergeHistory?.length ?? 0) > 0
    );
  }

  function canMergeWithNextPlan(plan: ClassRoomPlanFragmentFragment): boolean {
    const nextPlan = nextPlanOf(plan);
    return (
      !!plan.unlimitedLessonsPerWeekEnabled &&
      !!nextPlan?.unlimitedLessonsPerWeekEnabled &&
      (nextPlan.mergeHistory?.length ?? 0) === 0
    );
  }

  async function onDetachLastMerge(plan: ClassRoomPlanFragmentFragment) {
    if (
      !(await askConfirmation({
        title: "Detach the last merged plan?",
        message:
          "This plan goes back to how it was before its last merge and the merged purchase becomes a separate plan again. Lessons already used beyond this plan are taken from the detached one.",
        confirmLabel: "Detach",
      }))
    )
      return;

    try {
      await detachLastMergedPlan(plan.id);
    } catch (e) {
      return showToast((e as Error).message, "error");
    }
    showToast("The last merged plan is now a separate plan");
    await refetch();
  }

  async function onMergeWithNextPlan(plan: ClassRoomPlanFragmentFragment) {
    const nextPlan = nextPlanOf(plan);
    if (!nextPlan) return;
    const nextPlanStart = formatDate(dayjs(nextPlan.startedAtWeekUtc).utc());
    const nextPlanEnd = formatDate(dayjs(nextPlan.endedAtWeekUtc).utc());
    if (
      !(await askConfirmation({
        title: "Merge with the next plan?",
        message: `The plan from ${nextPlanStart} to ${nextPlanEnd} will be merged into this one: their lessons are added up and this plan will end on ${nextPlanEnd}. You can detach it again later.`,
        confirmLabel: "Merge",
      }))
    )
      return;

    try {
      await mergeUnlimitedPlans(plan.id, nextPlan.id);
    } catch (e) {
      return showToast((e as Error).message, "error");
    }
    showToast("Plans merged");
    await refetch();
  }

  if (loading) return <FullPageLoading />;
  if (!user) return null; //TODO return not found

  return (
    <Layout>
      <Card>
        <div>
          <ScreenTitleBlock
            ctaOnClick={() => toCreateClassRoomPlan(userId)}
            ctaLabel={"New plan"}
            title={`${user.givenName}'s active plans`}
            subTitle={
              <p>
                {_capitalize(user.givenName)} still has{" "}
                <b>{remainingLessons}</b> hours left to complete
              </p>
            }
            breadCrumbs={
              <BreadCrumbsWrapper>
                <BreadCrumbsItem onClick={toAllUsers} title={"Users"} />
                <BreadCrumbsItem title={"Active plans"} />
              </BreadCrumbsWrapper>
            }
          />
        </div>
      </Card>
      <div className={"mt-4"}>
        {plansByStartDate.length > 0 ? (
          <div className={"space-y-3"}>
            {plansByStartDate.map((p) => {
              return (
                <ClassRoomPlanListItem
                  deleting={deletingPlan}
                  onDelete={onDeletePlan}
                  onDetachLastMerge={
                    canDetachLastMerge(p) ? onDetachLastMerge : undefined
                  }
                  detachingLastMerge={detachingLastMerge}
                  onMergeWithNextPlan={
                    canMergeWithNextPlan(p) ? onMergeWithNextPlan : undefined
                  }
                  mergingWithNextPlan={mergingPlans}
                  key={p.id}
                  plan={p}
                  onEdit={(plan) =>
                    toUpdatePlanScreen({
                      userId: plan.userId,
                      planId: plan.id,
                    })
                  }
                />
              );
            })}
          </div>
        ) : (
          <Card>
            <div className={"flex justify-center"}>
              <InfoFeedback
                title={"No active plan"}
                message={`${user.givenName} has no active plan.`}
              />
            </div>
          </Card>
        )}
      </div>
      <div className={"mt-4"}>
        <Card>
          <UserFrozenWeekComponent userId={user.id} />
        </Card>
      </div>
    </Layout>
  );
}
